<template>
  <div>
    <SectionContainer bg-color="light" height="none" tag="div">
      <SBreadcrumbsSeo
        :breadcrumbs="[
          { label: 'Home', href: ziggy('mainSite.homepage') },
          {
            label: geoProductCategoryName,
            href: ziggy('client.slug', { slug: routeParams.geoProductSlug }),
          },
          {
            label: province?.name ?? routeParams.province,
            href: ziggy('mainSite.localSearch.province', routeParams),
          },
          { label: municipalityName },
        ]"
      />
    </SectionContainer>

    <SectionContainer
      bg-color="light"
      class="company-wall-municipality__hero-section"
      height="none"
    >
      <CompanyWallHero
        :benefits="municipalityBenefits"
        dynamic-length
        :form-url="formUrl"
        :heading="tr('main_site.company_wall.municipality.page_title')"
      >
        <template #subtitle>
          <p>
            {{
              tr("main_site.company_wall.municipality.hero.form_footer_text")
            }}
          </p>
        </template>

        <template #extra>
          <ProductTrustHighlight
            v-if="geoProductCategory"
            :average-score="geoProductCategory.averageRating"
            class="company-wall-municipality__hero-section__extra"
            :customer-plural="cmsLocalSearch?.customerPlural ?? null"
            :image-alt="
              cmsLocalSearch?.municipality.headerImage?.alt ??
              tr('main_site.company_wall.municipality.hero.expert_image_alt')
            "
            :image-src="
              useCloudinary(
                cmsLocalSearch?.municipality.headerImage?.url ??
                  'v1781170192/production/images/solvateers/solvateer_arms_crossed.png',
                { width: 640, height: 640 },
              )
            "
            :reviews-count="geoProductCategory.totalReviews"
            :specialists-count="geoProductCategory.totalCustomers"
          />
        </template>
      </CompanyWallHero>
    </SectionContainer>

    <SectionContainer>
      <div class="company-wall-municipality">
        <div v-if="introTitle" class="company-wall-municipality__intro">
          <SHeading
            class="company-wall-municipality__intro__title"
            level="2"
            size="2"
          >
            {{ introTitle }}
          </SHeading>
          <p
            v-if="introDescription"
            class="company-wall-municipality__intro__description"
          >
            {{ introDescription }}
          </p>
        </div>

        <span class="company-wall-municipality__result-count">
          {{ tr("main_site.company_wall.municipality.result_count") }}
        </span>
        <div
          v-if="loadedCompanies.length"
          class="company-wall-municipality__company-list"
        >
          <CompanyWallMunicipalityCard
            v-for="company in loadedCompanies"
            :key="company.companyCard.id"
            :company
            :geo-product-slug="routeParams.geoProductSlug"
          />
        </div>

        <p v-if="isEmpty" class="company-wall-municipality__no-results">
          {{ tr("main_site.company_wall.no_results") }}
        </p>

        <div v-if="isLoading" class="company-wall-municipality__company-list">
          <CompanyWallMunicipalityCardSkeleton
            v-for="index in 20"
            :key="index"
          />
        </div>

        <SErrorMessage
          v-else-if="hasCompaniesError"
          retry
          size="lg"
          @retry="onCompaniesRetryClick"
        />

        <SButton
          v-if="showLoadMore"
          class="company-wall-municipality__load-more"
          suffix-icon="far fa-chevron-down"
          @click="onLoadMoreClick"
          >{{ tr("main_site.company_wall.municipality.load_more") }}</SButton
        >
      </div>
    </SectionContainer>

    <CompanyWallMunicipalityReviews
      :form-url="formUrl"
      :route-params="routeParams"
    />

    <CompanyWallMunicipalityFaqs
      :geo-product-slug="routeParams.geoProductSlug"
      :municipality="routeParams.municipality"
      :province="routeParams.province"
    />

    <CompanyWallHowItWorks :form-url="formUrl" :route-params="routeParams" />

    <SectionContainer bg-color="light" size="md">
      <template v-if="otherMunicipalities?.length">
        <SHeading centered level="2" size="2">
          {{ municipalitiesTitle }}
        </SHeading>
        <p class="company-wall-municipality__link-description">
          {{
            tr("main_site.company_wall.municipality.municipalities_description")
          }}
        </p>
        <SButtonList
          :items="otherMunicipalities"
          :max-items="12"
          prefix-icon="far fa-location-dot"
          render-all
          :show-more-label="
            tr('main_site.company_wall.municipality.show_more_label')
          "
          :tag="NuxtLink"
        />
      </template>

      <template v-else-if="municipalitiesStatus !== 'error'">
        <SHeadingSkeleton size="2" width="60%" />
        <STextSkeleton font-size="lg" width="25rem" />
        <SButtonListSkeleton />
      </template>
    </SectionContainer>

    <SectionContainer bg-color="light" size="md">
      <template v-if="otherProjects?.length">
        <SHeading centered level="2" size="2">
          {{ tr("main_site.company_wall.municipality.products_title") }}
        </SHeading>
        <p class="company-wall-municipality__link-description">
          {{ tr("main_site.company_wall.municipality.products_description") }}
        </p>
        <SButtonList
          :items="otherProjects"
          :max-items="12"
          render-all
          :show-more-label="
            tr('main_site.company_wall.municipality.show_more_label')
          "
        />
      </template>

      <template v-else-if="productStatus !== 'error'">
        <SHeadingSkeleton size="2" width="60%" />
        <STextSkeleton font-size="lg" width="25rem" />
        <SButtonListSkeleton />
      </template>
    </SectionContainer>
  </div>
</template>

<script setup lang="ts">
import { useQuery } from "@pinia/colada";
import { useRoute, useRouter } from "#app";
import { NuxtLink } from "#components";
import { pick } from "radash";
import { computed, onMounted, ref, watchEffect } from "vue";

import { SButton } from "@solvari/common-fe";
import { useCloudinary } from "@solvari/common-fe/integrations";
import {
  SButtonList,
  SButtonListSkeleton,
  SErrorMessage,
  SHeading,
  SHeadingSkeleton,
  STextSkeleton,
  useGeoProductCategories,
  useToast,
} from "@solvari/components";
import {
  injectI18nLocale,
  localeToIso,
  useI18nMainSiteCompanyWall,
} from "@solvari/translations";

import type { CompanyCardCompany } from "~/features/company-wall/api/localSearch.api.ts";

import { useZiggy, ziggy } from "@/shared/libs/ziggy";
import { useMunicipalities } from "~/features/company-wall/api/geo.api.ts";
import {
  useCompanyCards,
  useCompanyCardsLocalSearchTop,
} from "~/features/company-wall/api/localSearch.api.ts";
import { useCmsLocalSearch } from "~/features/company-wall/api/localSearchCms.api.ts";
import { buildCompanyWallUrl } from "~/features/company-wall/companyWallUrl.ts";
import CompanyWallHero from "~/features/company-wall/components/company-wall-hero/CompanyWallHero.vue";
import ProductTrustHighlight from "~/features/company-wall/components/company-wall-hero/ProductTrustHighlight.vue";
import CompanyWallHowItWorks from "~/features/company-wall/components/company-wall-municipality/CompanyWallHowItWorks.vue";
import CompanyWallMunicipalityCard from "~/features/company-wall/components/company-wall-municipality/CompanyWallMunicipalityCard.vue";
import CompanyWallMunicipalityCardSkeleton from "~/features/company-wall/components/company-wall-municipality/CompanyWallMunicipalityCardSkeleton.vue";
import CompanyWallMunicipalityFaqs from "~/features/company-wall/components/company-wall-municipality/CompanyWallMunicipalityFaqs.vue";
import CompanyWallMunicipalityReviews from "~/features/company-wall/components/company-wall-municipality/CompanyWallMunicipalityReviews.vue";
import { useLocalSearchData } from "~/features/company-wall/composables/useLocalSearch";
import { pickSeeded } from "~/features/company-wall/helpers/seededRandom.ts";
import SBreadcrumbsSeo from "~/shared/components/SBreadcrumbsSeo.vue";
import SectionContainer from "~/shared/components/SectionContainer.vue";
import { useCurrentUrl, useHost } from "~/shared/composables/useHost.ts";
import {
  useSeoNormalized,
  useStructuredData,
} from "~/shared/composables/useMetaData.ts";
import { usePageContext } from "~/shared/composables/usePageContext.ts";
import { handleNuxtQuery } from "~/shared/libs/networkErrorHandling.ts";

import {
  metaDescriptionFallback,
  metaTitleFallback,
  seoWithCmsFallback,
  useLocalSearchTranslations,
} from "../composables/useLocalSearchTranslations.ts";

const route = useRoute();
const router = useRouter();
const routeParams = pick(route.params, [
  "geoProductSlug",
  "province",
  "municipality",
]) as { geoProductSlug: string; municipality: string; province: string };

const localeCode = injectI18nLocale().value;

const host = useHost();
const headZiggy = useZiggy();

usePageContext({ pageType: "local_search_municipality" });

const { geoProductCategory, province, municipality } = useLocalSearchData({
  ...routeParams,
  localeCode,
});

const geoProductCategoryName = computed(
  () => geoProductCategory.value?.name ?? routeParams.geoProductSlug,
);

const formUrl = ziggy("mainSite.geoProduct.leadForm", {
  geoProductSlug: routeParams.geoProductSlug,
  formSlug: undefined,
});

const municipalityName = computed(
  () => municipality.value?.name ?? routeParams.municipality,
);

const { tr, interpolateTranslation } = useLocalSearchTranslations(
  useI18nMainSiteCompanyWall,
  {
    ...routeParams,
    localeCode,
  },
);

onMounted(() => {
  if (route.query.companyDeleted) {
    useToast().add({
      severity: "warn",
      summary: tr("main_site.company_wall.company_deleted_alert_municipality"),
      life: 5000,
    });
    void router.replace({ query: {} });
  }
});

const { data: cmsLocalSearch } = useQuery(
  useCmsLocalSearch({
    uri: `/${routeParams.geoProductSlug}`,
    locale: localeCode,
  }),
);

// SEO
const baseKey = "main_site.company_wall.municipality";

const metaTitle = computed(() =>
  seoWithCmsFallback(
    cmsLocalSearch.value?.municipality.seoTitles,
    () =>
      (
        [
          `${baseKey}.seo_title_product_1`,
          `${baseKey}.seo_title_product_2`,
          `${baseKey}.seo_title_product_3`,
        ] as const
      ).map((key) => tr(key)),
    interpolateTranslation,
    metaTitleFallback,
  ),
);

const metaDescription = computed(() =>
  seoWithCmsFallback(
    cmsLocalSearch.value?.municipality.seoDescriptions,
    () => [
      tr(`${baseKey}.seo_description_1`),
      tr(`${baseKey}.seo_description_2`),
    ],
    interpolateTranslation,
    metaDescriptionFallback,
  ),
);

useSeoNormalized({
  title: metaTitle,
  description: metaDescription,
  index: () => geoProductCategory.value?.localSearchIndexable ?? true,
});

const customerPlural = computed(
  () => cmsLocalSearch.value?.customerPlural ?? geoProductCategoryName.value,
);

const currentUrl = useCurrentUrl();

// Hero
const municipalityBenefits = computed(() => [
  tr("main_site.company_wall.municipality.hero.benefits.benefit_1"),
  tr("main_site.company_wall.municipality.hero.benefits.benefit_2"),
  tr("main_site.company_wall.municipality.hero.benefits.benefit_3"),
]);

// Companies
const introTitle = computed(() => {
  const titles = cmsLocalSearch.value?.municipality.introTitles;
  if (!titles?.length) return null;
  const selected = pickSeeded(
    `${routeParams.geoProductSlug}-${routeParams.municipality}-title`,
    titles,
  );
  return interpolateTranslation(selected);
});

const introDescription = computed(() => {
  const descriptions = cmsLocalSearch.value?.municipality.introDescriptions;
  if (!descriptions?.length) return null;
  const selected = pickSeeded(
    `${routeParams.geoProductSlug}-${routeParams.municipality}-description`,
    descriptions,
  );
  return interpolateTranslation(selected);
});

const curatedSize = 20;
const listingPerPage = 10;

const { state: curatedState, refresh: curatedRefresh } = useQuery(
  useCompanyCardsLocalSearchTop({
    geoProductCategorySlug: routeParams.geoProductSlug,
    localeCode,
    municipalitySlug: routeParams.municipality,
  }),
);

const curatedCompanies = computed<CompanyCardCompany[]>(
  () => curatedState.value.data?.data ?? [],
);

// Load-more below the curated list: same distance listing, excludePrecomputed drops what's
// already shown -> pages are additive. Disabled until the first load-more click.
const listingPage = ref<number | null>(null);
const listedCompanies = ref<CompanyCardCompany[]>([]);

const { state: listingState, refresh: listingRefresh } = useQuery({
  ...useCompanyCards({
    geoProductCategorySlug: routeParams.geoProductSlug,
    localeCode,
    municipalitySlug: routeParams.municipality,
    provinceSlug: routeParams.province,
    excludePrecomputed: true,
    page: listingPage,
    perPage: listingPerPage,
  }),
  enabled: computed(() => listingPage.value !== null),
});

const loadedCompanies = computed(() => [
  ...curatedCompanies.value,
  ...listedCompanies.value,
]);

watchEffect(
  () => {
    const listing = listingState.value.data;
    if (!listing?.data.length || listingPage.value === null) {
      return;
    }
    if (listing.pagination.currentPage === listingPage.value) {
      listedCompanies.value.push(...listing.data);
    }
  },
  { flush: "sync" },
);

const isLoading = computed(() => curatedState.value.status === "pending");

// No stored selection, no distance fallback — nothing to show until the nightly compute
// covers this combo.
const isEmpty = computed(
  () =>
    curatedState.value.status === "success" &&
    loadedCompanies.value.length === 0,
);

const hasCompaniesError = computed(
  () =>
    curatedState.value.status === "error" ||
    listingState.value.status === "error",
);

function onCompaniesRetryClick() {
  if (curatedState.value.status === "error") {
    void curatedRefresh();
    return;
  }
  void listingRefresh();
}

function onLoadMoreClick() {
  listingPage.value = (listingPage.value ?? 0) + 1;
}

// Curated list short of its 20 slots = candidate pool exhausted, nothing left to page.
// Once paging starts, the listing's own pagination decides.
const showLoadMore = computed(() => {
  if (listingPage.value === null) {
    return curatedCompanies.value.length === curatedSize;
  }
  return (
    listingState.value.status === "success" &&
    listingState.value.data.pagination.currentPage <
      listingState.value.data.pagination.lastPage
  );
});

useStructuredData(() => {
  const companies = loadedCompanies.value;
  if (!companies.length) {
    return undefined;
  }
  return {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    "@type": "CollectionPage",
    name: `${customerPlural.value} in ${municipalityName.value}`,
    description: metaDescription.value,
    url: currentUrl(),
    inLanguage: localeToIso(localeCode),
    mainEntity: {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      "@type": "ItemList",
      name: `${customerPlural.value} in ${municipalityName.value}`,
      numberOfItems: companies.length,
      itemListElement: companies.map((company, index) => ({
        // eslint-disable-next-line @typescript-eslint/naming-convention
        "@type": "ListItem",
        position: index + 1,
        name: company.companyCard.name,
        url: `https://${host}${buildCompanyWallUrl({
          companySlug: company.companyCard.slug,
          isSupportedLocale: company.isSupportedLocale,
          geoProductSlug: routeParams.geoProductSlug,
          provinceSlug: company.provinceSlug,
          municipalitySlug: company.municipalitySlug,
          subMunicipalitySlug: company.subMunicipalitySlug,
          ziggyFn: headZiggy,
        })}`,
      })),
    },
  };
});

// Municipalities links
const municipalitiesTitle = computed(() =>
  tr("main_site.company_wall.municipality.municipalities_title"),
);

const { data: municipalitiesRaw, status: municipalitiesStatus } =
  useMunicipalities({
    provinceSlug: routeParams.province,
    localeCode,
  });

const otherMunicipalities = computed(() =>
  municipalitiesRaw.value
    ?.filter((item) => item.slug !== routeParams.municipality)
    .map((item) => ({
      ...item,
      to: ziggy("mainSite.localSearch.municipality", {
        geoProductSlug: routeParams.geoProductSlug,
        province: routeParams.province,
        municipality: item.slug,
      }),
    })),
);

// Products links
const { data: productsData, status: productStatus } = useQuery(
  useGeoProductCategories(
    { localeCode, hasLocalSearch: true },
    handleNuxtQuery,
  ),
);

const otherProjects = computed(() =>
  productsData.value
    ?.filter((product) => product.slug !== routeParams.geoProductSlug)
    .map((product) => ({
      name: product.name,
      to: ziggy("mainSite.localSearch.municipality", {
        geoProductSlug: product.slug,
        province: routeParams.province,
        municipality: routeParams.municipality,
      }),
    })),
);
</script>

<style lang="postcss">
.company-wall-municipality {
  @apply flex flex-col;

  &__intro {
    &__title {
      @apply text-center;
    }

    &__description {
      @apply mb-4 text-center text-lg text-slate-600;
    }
  }

  &__result-count {
    @apply mb-1 text-sm text-slate-700;
  }

  &__company-list {
    @apply mb-6 grid gap-6 grid-cols-auto-104 grid-cols-auto-grow;
  }

  &__load-more {
    @apply w-full;

    @screen 900 {
      @apply mx-auto w-fit px-4;
    }
  }

  &__link-description {
    @apply mb-6 text-center text-lg text-slate-700;
  }

  &__hero-section {
    @apply pb-0 pt-2;

    @screen 1000 {
      @apply pb-0 pt-4;
    }

    &__extra {
      @apply h-fit;
    }
  }
}
</style>
